import axios from 'axios'
import wx from 'weixin-js-sdk'
import { Dialog } from "vant"

/**
 * 后端API基础URL配置
 * 开发环境使用代理路径，生产环境使用真实域名
 */
const API_BASE_URL = '/api'

/**
 * 微信JS-SDK支持的API列表
 * 包含个人认证公众号可用的所有功能接口
 */
export const apiList = [
    // 分享功能
    'updateAppMessageShareData', // 分享给朋友（新版）
    'updateTimelineShareData', // 分享到朋友圈（新版）
    'onMenuShareTimeline', // 分享到朋友圈（旧版，即将废弃）
    'onMenuShareAppMessage', // 分享给朋友（旧版，即将废弃）
    'onMenuShareQQ', // 分享到QQ
    'onMenuShareWeibo', // 分享到微博
    'onMenuShareQZone', // 分享到QQ空间

    // 图像功能
    'chooseImage', // 选择图片
    'previewImage', // 预览图片
    'uploadImage', // 上传图片
    'downloadImage', // 下载图片

    // 音频功能
    'startRecord', // 开始录音
    'stopRecord', // 停止录音
    'onVoiceRecordEnd', // 录音完成回调
    'playVoice', // 播放语音
    'pauseVoice', // 暂停播放
    'stopVoice', // 停止播放
    'onVoicePlayEnd', // 播放完成回调
    'uploadVoice', // 上传语音
    'downloadVoice', // 下载语音

    // 智能功能
    'translateVoice', // 语音识别翻译

    // 扫一扫
    'scanQRCode', // 扫描二维码

    // 基础功能
    'getNetworkType', // 获取网络状态
    'openLocation', // 打开地理位置
    'getLocation', // 获取地理位置
    'hideOptionMenu', // 隐藏右上角菜单
    'showOptionMenu', // 显示右上角菜单
    'hideMenuItems', // 隐藏菜单项
    'showMenuItems', // 显示菜单项
    'hideAllNonBaseMenuItem', // 隐藏所有非基础菜单
    'showAllNonBaseMenuItem', // 显示所有非基础菜单
    'closeWindow' // 关闭窗口
]

// 全局状态管理
let isSDKReady = false // SDK是否已初始化完成
let currentSignature = null // 当前缓存的签名数据
let signatureTimestamp = 0 // 签名获取时间戳
const SIGNATURE_EXPIRE_TIME = 7000 * 1000 // 签名有效期（7000秒，约2小时）

/**
 * 获取当前页面URL（移除hash部分）
 * @returns {string} 当前页面完整URL（不含hash）
 */
export const getCurrentUrl = () => {
    return window.location.href.split('#')[0]
}


/**
 * 获取微信JS-SDK签名
 * @param {string} url - 需要签名的页面URL
 * @returns {Promise<Object>} 签名数据对象
 * @throws {Error} 获取签名失败时抛出错误
 */
const getSignature = async (url) => {
    try {
        // 检查是否有有效的签名缓存
        const now = Date.now()
        if (currentSignature && now - signatureTimestamp < SIGNATURE_EXPIRE_TIME) {
            return currentSignature
        }

        // 修复：正确获取axios响应数据
        const response = await axios.post(`${API_BASE_URL}/signature`, { url })
        console.log('签名接口响应:', response.data) // 添加调试日志

        if (response.data.code === 0) {
            currentSignature = response.data // 保存完整的响应数据
            signatureTimestamp = now
            return response.data
        } else {
            await Dialog({ message: `${response.data.message || '获取微信js-sdk签名失败'}` })
            throw new Error(response.data.message || '获取微信js-sdk签名失败')
        }
    } catch (error) {
        console.error('获取微信签名失败详情:', error)
        if (error.response) {
            console.error('响应状态:', error.response.status)
            console.error('响应数据:', error.response.data)
        }
        await Dialog({ message: `${error.message}`, title: '获取微信js-sdk签名失败' })
        throw error
    }
}

/**
 * 配置微信JS-SDK
 * @returns {Promise<void>}
 * @throws {Error} 配置失败时抛出错误
 */
const configWechatSDK = async () => {
    try {
        const url = getCurrentUrl()
        console.log('配置SDK的URL:', url) // 调试日志

        const signatureData = await getSignature(url)
        console.log('获取到的签名数据:', signatureData) // 调试日志

        const { appId, timestamp, nonceStr, signature } = signatureData

        return new Promise((resolve, reject) => {
            wx.config({
                debug: process.env.NODE_ENV === 'development',
                appId,
                timestamp,
                nonceStr,
                signature,
                jsApiList: [
                    'updateAppMessageShareData',
                    'updateTimelineShareData',
                    'onMenuShareTimeline',
                    'onMenuShareAppMessage'
                    // 可以根据需要添加其他API
                ]
            })

            wx.ready(() => {
                console.log('✅ 微信SDK配置成功')
                isSDKReady = true
                resolve()
            })

            wx.error((error) => {
                console.error('❌ 微信SDK配置失败:', error)
                reject(error)
            })
        })
    } catch (error) {
        console.error('配置微信SDK失败:', error)
        throw error
    }
}

/**
 * 确保微信SDK已初始化
 * @returns {Promise<void>}
 */
const ensureSDKReady = async () => {
    if (!isSDKReady) {
        console.log('🔄 初始化微信SDK...')
        await configWechatSDK()
    }
}

/**
 * 将微信原生API包装为Promise形式
 * @param {string} apiName - 微信API名称
 * @param {Object} options - API参数
 * @returns {Promise<any>}
 */
const promisifyWxAPI = (apiName, options = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            console.log(`🔄 调用微信API: ${apiName}`, options)
            await ensureSDKReady()

            wx[apiName]({
                ...options,
                success: (res) => {
                    console.log(`✅ ${apiName} 成功:`, res)
                    resolve(res)
                },
                fail: (err) => {
                    console.error(`❌ ${apiName} 失败:`, err)
                    reject(err)
                }
            })
        } catch (error) {
            console.error(`❌ ${apiName} 异常:`, error)
            reject(error)
        }
    })
}
/**
 * 微信JS-SDK功能封装对象
 * 提供简单易用的API调用方式，内部自动处理签名和初始化
 */
export const wxApi = {
    /**
     * 初始化微信JS-SDK
     * @returns {Promise<boolean>} 初始化是否成功
     * @example
     * await wxApi.init()
     */
    init: async () => {
        await ensureSDKReady()
        return true
    },

    /**
     * 更新分享给朋友的数据
     * @param {Object} options - 分享配置
     * @param {string} options.title - 分享标题
     * @param {string} options.desc - 分享描述
     * @param {string} options.link - 分享链接
     * @param {string} options.imgUrl - 分享图标
     * @returns {Promise<any>}
     * @example
     * await wxApi.updateAppMessageShare({
     *   title: '分享标题',
     *   desc: '分享描述',
     *   link: 'https://example.com',
     *   imgUrl: 'https://example.com/icon.jpg'
     * })
     */
    updateAppMessageShare: async (options) => {
        // 修正参数名：imageUrl → imgUrl
        const correctedOptions = {
            ...options,

        }

        console.log('🔄 调用updateAppMessageShare:', correctedOptions)
        return promisifyWxAPI('updateAppMessageShareData', correctedOptions)
    },

    /**
     * 更新分享到朋友圈的数据
     * @param {Object} options - 分享配置
     * @param {string} options.title - 分享标题
     * @param {string} options.link - 分享链接
     * @param {string} options.imgUrl - 分享图标
     * @returns {Promise<any>}
     * @example
     * await wxApi.updateTimelineShare({
     *   title: '朋友圈分享标题',
     *   link: 'https://example.com',
     *   imgUrl: 'https://example.com/icon.jpg'
     * })
     */
    updateTimelineShare: async (options) => {
        return promisifyWxAPI('updateTimelineShareData', options)
    },

    /**
     * 设置完整的分享配置（同时设置新版和旧版分享）
     * @param {Object} shareConfig - 分享配置对象
     * @param {string} shareConfig.title - 分享标题
     * @param {string} shareConfig.desc - 分享描述
     * @param {string} shareConfig.link - 分享链接
     * @param {string} shareConfig.imgUrl - 分享图标
     * @param {Function} shareConfig.success - 分享成功回调
     * @param {Function} shareConfig.cancel - 取消分享回调
     * @returns {Promise<void>}
     * @example
     * await wxApi.setShareConfig({
     *   title: '自定义标题',
     *   desc: '自定义描述',
     *   link: window.location.href,
     *   imgUrl: '/share-image.jpg',
     *   success: () => console.log('分享成功'),
     *   cancel: () => console.log('取消分享')
     * })
     */
    setShareConfig: async (shareConfig) => {
        await ensureSDKReady()

        const defaultConfig = {
            title: document.title,
            desc: '',
            link: window.location.href,
            imgUrl: ''
        }

        const config = { ...defaultConfig, ...shareConfig }

        // 设置新版分享
        await promisifyWxAPI('updateAppMessageShareData', {
            title: config.title,
            desc: config.desc,
            link: config.link,
            imgUrl: config.imgUrl
        })

        await promisifyWxAPI('updateTimelineShareData', {
            title: config.title,
            link: config.link,
            imgUrl: config.imgUrl
        })

        // 设置旧版分享（兼容性）
        promisifyWxAPI('onMenuShareTimeline', {
            title: config.title,
            link: config.link,
            imgUrl: config.imgUrl,
            success: config.success,
            cancel: config.cancel
        })

        promisifyWxAPI('onMenuShareAppMessage', {
            title: config.title,
            desc: config.desc,
            link: config.link,
            imgUrl: config.imgUrl,
            success: config.success,
            cancel: config.cancel
        })
    },

    /**
     * 选择图片
     * @param {Object} options - 选择配置
     * @param {number} options.count - 选择数量（默认9）
     * @param {string[]} options.sizeType - 图片尺寸（['original', 'compressed']）
     * @param {string[]} options.sourceType - 来源（['album', 'camera']）
     * @returns {Promise<Object>} 包含localIds的结果对象
     * @example
     * const result = await wxApi.chooseImage({ count: 1 })
     * console.log('选择的图片:', result.localIds)
     */
    chooseImage: async (options = {}) => {
        const defaultOptions = {
            count: 9,
            sizeType: ['original', 'compressed'],
            sourceType: ['album', 'camera']
        }
        return promisifyWxAPI('chooseImage', { ...defaultOptions, ...options })
    },

    /**
     * 预览图片
     * @param {Object} options - 预览配置
     * @param {string[]} options.urls - 图片URL列表
     * @param {string} options.current - 当前显示图片URL
     * @returns {Promise<any>}
     * @example
     * await wxApi.previewImage({
     *   current: 'https://example.com/image1.jpg',
     *   urls: ['https://example.com/image1.jpg', 'https://example.com/image2.jpg']
     * })
     */
    previewImage: async (options) => {
        return promisifyWxAPI('previewImage', options)
    },

    /**
     * 上传图片
     * @param {Object} options - 上传配置
     * @param {string} options.localId - 本地图片ID
     * @param {number} options.isShowProgressTips - 是否显示进度提示（0/1）
     * @returns {Promise<Object>} 包含serverId的结果对象
     * @example
     * const result = await wxApi.uploadImage({ localId: 'xxx' })
     * console.log('服务器图片ID:', result.serverId)
     */
    uploadImage: async (options) => {
        return promisifyWxAPI('uploadImage', options)
    },

    /**
     * 下载图片
     * @param {Object} options - 下载配置
     * @param {string} options.serverId - 服务器图片ID
     * @param {number} options.isShowProgressTips - 是否显示进度提示（0/1）
     * @returns {Promise<Object>} 包含localId的结果对象
     * @example
     * const result = await wxApi.downloadImage({ serverId: 'xxx' })
     * console.log('本地图片ID:', result.localId)
     */
    downloadImage: async (options) => {
        return promisifyWxAPI('downloadImage', options)
    },

    /**
     * 开始录音
     * @returns {Promise<any>}
     * @example
     * await wxApi.startRecord()
     */
    startRecord: async () => {
        return promisifyWxAPI('startRecord')
    },

    /**
     * 停止录音
     * @returns {Promise<Object>} 包含localId的结果对象
     * @example
     * const result = await wxApi.stopRecord()
     * console.log('录音文件ID:', result.localId)
     */
    stopRecord: async () => {
        return promisifyWxAPI('stopRecord')
    },

    /**
     * 播放语音
     * @param {string} localId - 本地语音文件ID
     * @returns {Promise<any>}
     * @example
     * await wxApi.playVoice('local_voice_id')
     */
    playVoice: async (localId) => {
        return promisifyWxAPI('playVoice', { localId })
    },

    /**
     * 暂停播放语音
     * @param {string} localId - 本地语音文件ID
     * @returns {Promise<any>}
     * @example
     * await wxApi.pauseVoice('local_voice_id')
     */
    pauseVoice: async (localId) => {
        return promisifyWxAPI('pauseVoice', { localId })
    },

    /**
     * 停止播放语音
     * @param {string} localId - 本地语音文件ID
     * @returns {Promise<any>}
     * @example
     * await wxApi.stopVoice('local_voice_id')
     */
    stopVoice: async (localId) => {
        return promisifyWxAPI('stopVoice', { localId })
    },

    /**
     * 上传语音文件
     * @param {string} localId - 本地语音文件ID
     * @param {number} isShowProgressTips - 是否显示进度提示（默认1）
     * @returns {Promise<Object>} 包含serverId的结果对象
     * @example
     * const result = await wxApi.uploadVoice('local_voice_id')
     * console.log('服务器语音ID:', result.serverId)
     */
    uploadVoice: async (localId, isShowProgressTips = 1) => {
        return promisifyWxAPI('uploadVoice', { localId, isShowProgressTips })
    },

    /**
     * 下载语音文件
     * @param {string} serverId - 服务器语音文件ID
     * @param {number} isShowProgressTips - 是否显示进度提示（默认1）
     * @returns {Promise<Object>} 包含localId的结果对象
     * @example
     * const result = await wxApi.downloadVoice('server_voice_id')
     * console.log('本地语音ID:', result.localId)
     */
    downloadVoice: async (serverId, isShowProgressTips = 1) => {
        return promisifyWxAPI('downloadVoice', { serverId, isShowProgressTips })
    },

    /**
     * 语音识别翻译
     * @param {string} localId - 本地语音文件ID
     * @param {number} isShowProgressTips - 是否显示进度提示（默认1）
     * @returns {Promise<Object>} 包含翻译结果的对象
     * @example
     * const result = await wxApi.translateVoice('local_voice_id')
     * console.log('翻译结果:', result.translateResult)
     */
    translateVoice: async (localId, isShowProgressTips = 1) => {
        return promisifyWxAPI('translateVoice', { localId, isShowProgressTips })
    },

    /**
     * 扫描二维码
     * @param {number} needResult - 是否需要结果（0-不需要，1-需要）
     * @param {string[]} scanType - 扫描类型（['qrCode', 'barCode']）
     * @returns {Promise<Object>} 扫描结果对象
     * @example
     * const result = await wxApi.scanQRCode(1)
     * console.log('扫描结果:', result.resultStr)
     */
    scanQRCode: async (needResult = 0, scanType = ['qrCode', 'barCode']) => {
        return promisifyWxAPI('scanQRCode', { needResult, scanType })
    },

    /**
     * 获取地理位置
     * @param {string} type - 坐标类型（'wgs84'或'gcj02'）
     * @returns {Promise<Object>} 位置信息对象
     * @example
     * const location = await wxApi.getLocation()
     * console.log('纬度:', location.latitude, '经度:', location.longitude)
     */
    getLocation: async (type = 'wgs84') => {
        return promisifyWxAPI('getLocation', { type })
    },

    /**
     * 打开地理位置
     * @param {Object} options - 位置信息
     * @param {number} options.latitude - 纬度
     * @param {number} options.longitude - 经度
     * @param {string} options.name - 位置名称
     * @param {string} options.address - 地址详情
     * @param {number} options.scale - 地图缩放级别
     * @param {string} options.infoUrl - 信息页URL
     * @returns {Promise<any>}
     * @example
     * await wxApi.openLocation({
     *   latitude: 39.90469,
     *   longitude: 116.40717,
     *   name: '北京市',
     *   address: '北京市东城区',
     *   scale: 15
     * })
     */
    openLocation: async (options) => {
        return promisifyWxAPI('openLocation', options)
    },

    /**
     * 获取网络类型
     * @returns {Promise<Object>} 网络状态对象
     * @example
     * const network = await wxApi.getNetworkType()
     * console.log('网络类型:', network.networkType)
     */
    getNetworkType: async () => {
        return promisifyWxAPI('getNetworkType')
    },

    /**
     * 隐藏右上角菜单
     * @returns {Promise<any>}
     * @example
     * await wxApi.hideOptionMenu()
     */
    hideOptionMenu: async () => {
        return promisifyWxAPI('hideOptionMenu')
    },

    /**
     * 显示右上角菜单
     * @returns {Promise<any>}
     * @example
     * await wxApi.showOptionMenu()
     */
    showOptionMenu: async () => {
        return promisifyWxAPI('showOptionMenu')
    },

    /**
     * 隐藏指定菜单项
     * @param {string[]} menuList - 需要隐藏的菜单项列表
     * @returns {Promise<any>}
     * @example
     * await wxApi.hideMenuItems(['menuItem:share:appMessage', 'menuItem:share:timeline'])
     */
    hideMenuItems: async (menuList) => {
        return promisifyWxAPI('hideMenuItems', { menuList })
    },

    /**
     * 显示指定菜单项
     * @param {string[]} menuList - 需要显示的菜单项列表
     * @returns {Promise<any>}
     * @example
     * await wxApi.showMenuItems(['menuItem:share:appMessage', 'menuItem:share:timeline'])
     */
    showMenuItems: async (menuList) => {
        return promisifyWxAPI('showMenuItems', { menuList })
    },

    /**
     * 隐藏所有非基础菜单项
     * @returns {Promise<any>}
     * @example
     * await wxApi.hideAllNonBaseMenuItem()
     */
    hideAllNonBaseMenuItem: async () => {
        return promisifyWxAPI('hideAllNonBaseMenuItem')
    },

    /**
     * 显示所有非基础菜单项
     * @returns {Promise<any>}
     * @example
     * await wxApi.showAllNonBaseMenuItem()
     */
    showAllNonBaseMenuItem: async () => {
        return promisifyWxAPI('showAllNonBaseMenuItem')
    },

    /**
     * 关闭当前窗口
     * @returns {Promise<any>}
     * @example
     * await wxApi.closeWindow()
     */
    closeWindow: async () => {
        return promisifyWxAPI('closeWindow')
    },

    /**
     * 检查JSAPI是否可用
     * @param {string[]} jsApiList - 需要检查的API列表
     * @returns {Promise<Object>} 检查结果对象
     * @example
     * const result = await wxApi.checkJsApi(['chooseImage', 'getLocation'])
     * console.log('API支持情况:', result.checkResult)
     */
    checkJsApi: async (jsApiList) => {
        return promisifyWxAPI('checkJsApi', { jsApiList })
    },

    // ==================== 事件监听方法（非Promise形式） ====================

    /**
     * 监听"分享到朋友圈"按钮点击事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onMenuShareTimeline(function(res) {
     *   console.log('用户点击了分享到朋友圈')
     * })
     */
    onMenuShareTimeline: (callback) => {
        wx.onMenuShareTimeline(callback)
    },

    /**
     * 监听"分享给朋友"按钮点击事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onMenuShareAppMessage(function(res) {
     *   console.log('用户点击了分享给朋友')
     * })
     */
    onMenuShareAppMessage: (callback) => {
        wx.onMenuShareAppMessage(callback)
    },

    /**
     * 监听"分享到QQ"按钮点击事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onMenuShareQQ(function(res) {
     *   console.log('用户点击了分享到QQ')
     * })
     */
    onMenuShareQQ: (callback) => {
        wx.onMenuShareQQ(callback)
    },

    /**
     * 监听"分享到微博"按钮点击事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onMenuShareWeibo(function(res) {
     *   console.log('用户点击了分享到微博')
     * })
     */
    onMenuShareWeibo: (callback) => {
        wx.onMenuShareWeibo(callback)
    },

    /**
     * 监听"分享到QQ空间"按钮点击事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onMenuShareQZone(function(res) {
     *   console.log('用户点击了分享到QQ空间')
     * })
     */
    onMenuShareQZone: (callback) => {
        wx.onMenuShareQZone(callback)
    },

    /**
     * 监听录音结束事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onVoiceRecordEnd(function(res) {
     *   console.log('录音结束，本地ID:', res.localId)
     * })
     */
    onVoiceRecordEnd: (callback) => {
        wx.onVoiceRecordEnd(callback)
    },

    /**
     * 监听语音播放结束事件
     * @param {Function} callback - 回调函数
     * @example
     * wxApi.onVoicePlayEnd(function(res) {
     *   console.log('语音播放结束，本地ID:', res.localId)
     * })
     */
    onVoicePlayEnd: (callback) => {
        wx.onVoicePlayEnd(callback)
    }
}

export default wxApi
